/**
 * @file SSODropdown.js
 * @description A dropdown component for selecting an SSO option.
 */

import React, { useState, useEffect } from 'react';
import {
    fetchSystemSSOList,
    fetchClientSSOList,
} from '../../../services/ssoService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * Represents a dropdown component for selecting an SSO option.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {string|null} [props.selectedSSO] - The ID of the selected SSO option or null if no option is selected.
 * @param {function} props.onSSOChange - The callback function to be called when the selected SSO option changes.
 * @param {string} [props.label="SSO"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNull=false] - Determines whether null selection is allowed.
 * @param {boolean} [props.client=false] - Determines whether to fetch the client or system SSO list.
 * @param {string} [props.supportingText=""] - The supporting text for the dropdown.
 * @param {boolean} [props.displayRelative=true] - Determines whether to display the LoadingWrapper relative or block.
 * @returns {JSX.Element} The rendered SSODropdown component.
 * @prop {string} maxOpenHeight - Maximum height of option list when Dropdown is open, in px. Default is '300px'.
 * @prop {boolean} overlappingOptions - Indicating that the option list overlays the content beneath with absolute position, rather than being a relatively positioned element occupying space in the parent container.
 */
const SSODropdown = ({
    className,
    inlineLabel = true,
    selectedSSO,
    onSSOChange,
    label = 'SSO',
    isRequired = false,
    allowNull = false,
    client = false,
    supportingText = '',
    displayRelative = true,
    placeholder = 'Select SSO',
    maxOpenHeight = '300px',
    overlappingOptions = true,
    disabled = false,
}) => {
    const [ssoOptions, setSSOOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getSystemSSOList = async () => {
            setIsLoading(true);
            try {
                if (client) {
                    const data = await fetchClientSSOList();
                    const filteredSSOs = data.filter(
                        sso => sso.status === 'Live',
                    );
                    setSSOOptions(filteredSSOs);
                } else {
                    const data = await fetchSystemSSOList();
                    setSSOOptions(data);
                }
            } catch (error) {
                console.error('Could not fetch SSO options:', error);
                Toast.error(getText('msg_85b13cd12e86'));
            } finally {
                setIsLoading(false);
            }
        };

        getSystemSSOList();
    }, []);

    const getDefaultLabel = () => {
        if (!selectedSSO) {
            return placeholder || 'Select SSO';
        }
        const matchingSSO = ssoOptions.find(sso => sso.id === selectedSSO);
        return matchingSSO ? matchingSSO.name : placeholder || 'Select SSO';
    };

    const dropdownOptions = allowNull
        ? [
              { value: null, label: getText('msg_fb954a0b17de') },
              ...ssoOptions.map(sso => ({
                  value: sso.id,
                  label: sso.name,
              })),
          ]
        : ssoOptions.map(sso => ({
              value: sso.id,
              label: sso.name,
          }));

    const handleSSOChange = value => {
        if (allowNull && value === null) {
            onSSOChange(null);
        } else {
            onSSOChange(value);
        }
    };

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            {label && (
                <label htmlFor="sso-dropdown">
                    {label}
                    {isRequired && <span className="text-danger">*</span>}
                </label>
            )}
            <LoadingWrapper
                isLoading={isLoading}
                displayRelative={displayRelative}
            >
                <Dropdown
                    id="sso-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={dropdownOptions}
                    value={selectedSSO}
                    onChange={handleSSOChange}
                    allowSearch={true}
                    disabled={disabled || ssoOptions.length === 0 || isLoading}
                    displayRelative={displayRelative}
                    maxOpenHeight={maxOpenHeight}
                    overlappingOptions={overlappingOptions}
                />
            </LoadingWrapper>
            {supportingText && (
                <div className="supportingText">{supportingText}</div>
            )}
        </div>
    );
};

export default SSODropdown;
